import React, {useContext, useEffect, useState} from "react";
import {useDispatch} from "react-redux";

import {ProductContext} from "../../../../../pages/ProductStencil";
import {useI18N} from "../../../../../my-puratos/hoc/withI18N";
import {addBasketItems} from "../../../../../../api/ECOM/addBasketItems.api";
import {priceRequest} from "../../../../../../api/ECOM/priceRequest.api";
import {GetPackagingItem, Product} from "../../../../../../interfaces/ecom/schema";
import {refreshCart} from "../../../../../../actions/cart";
import {pdpGA4TrackAddToCart, pdpGA4TrackPriceRequest} from "../../../../../../analytics/ga4/events/gaEventsPDP";
import {setProductCrossSell} from "../../../../../../actions/analytics";
import {GA4ComponentType, mapComponentTypeToCrossSellType} from "../../../../../../analytics/ga4/types/component";

interface ICardProduct extends Product {
    Packagings: Array<any>
}

interface IProps {
    quantity?: number,
    setAddedToCart?: (_: number) => void,
    addedToCart?: number,
    product?: ICardProduct,
    packagingItem?: GetPackagingItem,
    componentType?: GA4ComponentType,
    showBasketOverlay?: boolean
}

export const AddToCartButton = ({
                                    product,
                                    quantity = 1,
                                    addedToCart,
                                    setAddedToCart,
                                    componentType = GA4ComponentType.PDP_ADD_TO_CART,
                                    showBasketOverlay = true
                                }: IProps) => {
    const translate = useI18N();
    const productData = useContext(ProductContext);
    const {Packaging, activeProductIndex, SapDetails, setShowBasketOverlay} = productData;
    const IsObsolete = !!product ? product.Packagings[0].IsObsolete : SapDetails[0].IsObsolete

    if (IsObsolete) {
        return null;
    }

    const {
        HasPrice: packagingHasPrice,
        StockType: packagingStockType,
        MaterialNumber: packagingMaterialNumber
    } = Packaging[activeProductIndex];

    if (!!product && !product.Packagings.length) {
        return null;
    }
    const HasPrice = !!product ? product.Packagings[0].HasPrice : packagingHasPrice;
    const MaterialNumber = !!product ? product.Packagings[0].MaterialNumber : packagingMaterialNumber;
    const StockType = !!product ? product.Packagings[0].StockType : packagingStockType;
    const [addingToCart, setAddingToCart] = useState(false);
    const [addingToCartSuccess, setAddingToCartSuccess] = useState(false);
    const [addingToCartError, setAddingToCartError] = useState(false);
    const [priceRequested, setPriceRequested] = useState(false);
    const dispatch = useDispatch();

    useEffect(() => {
        const priceRequested = window.localStorage.getItem(`priceRequested:${MaterialNumber}`);
        if (priceRequested) {
            setPriceRequested(JSON.parse(priceRequested));
        }
    }, []);

    useEffect(() => {
        if (addingToCartSuccess) {
            dispatch(refreshCart());
        }

    }, [addingToCartSuccess]);

    const handleAddToCart = async () => {
        setAddingToCart(true);
        try {
            const response = await addBasketItems(Number(MaterialNumber), quantity);

            if (response.IsSuccess) {
                setAddingToCartSuccess(true);
                setAddingToCart(false);
                showBasketOverlay && setShowBasketOverlay(true, quantity);

                setTimeout(() => {
                    setAddingToCartSuccess(false);
                }, 5000);

                pdpGA4TrackAddToCart(
                    product ? product.Packagings[0] : productData.Packaging[activeProductIndex],
                    componentType,
                    quantity
                );

                if ((componentType === GA4ComponentType.PDP_ALSO_BOUGHT || componentType === GA4ComponentType.PDP_RELATED_PRODUCTS) && product) {
                    dispatch(
                        setProductCrossSell({
                            ...product,
                            materialNumber: product.Packagings[0].MaterialNumber,
                            type: mapComponentTypeToCrossSellType(componentType)
                        })
                    );
                }

                setAddedToCart && setAddedToCart(addedToCart + quantity);
            }
        } catch (e) {
            console.error(e);
            setAddingToCart(false);
            setAddingToCartError(true);

            setTimeout(() => {
                setAddingToCartError(false);
            }, 5000);
        }
    };

    const selectedProduct = product || productData.product;
    const selectedPackaging = product? product.Packagings[0] : productData.Packaging[activeProductIndex];

    const handleRequestPrice = async () => {
        try {
            await priceRequest(Number(MaterialNumber));
            pdpGA4TrackPriceRequest(
                {...selectedProduct, Packagings: [selectedPackaging]},
                componentType,
            );
            
            setPriceRequested(true);
            window.localStorage.setItem(`priceRequested:${MaterialNumber}`, JSON.stringify(true));//expand with material number
        } catch (e) {
            console.error(e);
            setPriceRequested(false);
        }
    }

    const buttonLabel = () => {
        switch (true) {
            case addingToCartSuccess:
                return translate('Added to cart');
            case priceRequested:
                return translate('Price requested');
            case !HasPrice && !priceRequested:
                return translate('Request price');
            case addingToCartError:
                return translate('Failed to add to cart');
            case addingToCart:
                return translate('Adding to cart');
            case StockType === "BLOCKSALE":
                return translate('Out of stock');
            default:
                return translate('Add to cart');
        }
    }

    const isDisabled = addingToCartError || addingToCart || addingToCartSuccess || priceRequested || IsObsolete;

    return (
        <button
            className={`btn btn-primary ${addingToCartSuccess || priceRequested ? "add-to-cart__button add-to-cart__button--added" : "add-to-cart__button"}${StockType === "BLOCKSALE" ? " add-to-cart__button--blocksale" : ""}`}
            onClick={HasPrice ? handleAddToCart : handleRequestPrice}
            disabled={isDisabled}
        >
            {buttonLabel()}
        </button>
    )
}
